import CurrencySelect, { Option } from '@/components/inputs/CurrencySelect';
import ErrorMessage from '@/components/inputs/ErrorMessage';
import { convertedQtyFormatterConfig } from '@/config/config';
import { BalanceItem, CollapsedBalances } from '@/state/reducers/balanceSlice';
import { store } from '@/state/store';
import { mergeRefs, parseShortFormNumber } from '@/utils/common';
import { useCurrency } from '@/utils/hooks/useCurrency';
import { useCurrencyConfigOptions } from '@/utils/hooks/useCurrencyConfigOptions';
import useLanguage from '@/utils/hooks/useLanguage';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { useEffect, useMemo, useRef } from 'react';
import { useLocale, useNumberField } from 'react-aria';
import { Controller, useFormContext } from 'react-hook-form';
import { useNumberFieldState } from 'react-stately';

interface ConvertFromProps {
    balances: any[];
    // balances: BalanceItem[] | CollapsedBalances[];
    setConvertFrom(e: Option): void;
}

const mode = window.config?.modules?.conversion?.mode || 'enableAll';

const ConvertFrom = ({ balances, setConvertFrom }: ConvertFromProps) => {
    const { watch, setValue, control } = useFormContext();
    const [convertFrom, amount] = watch(['convertFrom', 'amount']);

    const lang = useLanguage();
    const { currencyOptions } = useCurrencyConfigOptions();
    const { min_order_size, order_decimals, increments } = useCurrency(convertFrom?.value);

    const convertFromOptions = useMemo(() => {
        const availablePairs = Object.keys(store.getState().marketPair.bidAsk);
        const options = currencyOptions
            .filter((ccy) => availablePairs.find((pair) => pair.includes(ccy.celer)))
            .sort((a, b) => a.label.localeCompare(b.label));
        if (mode === 'enableAll') {
            return options;
        } else {
            return options.filter((b) => {
                const balance = balances.find((balance) => balance.currency === b.value) as BalanceItem &
                    CollapsedBalances;
                return b.value === 'BTC' || (balance?.netPosition || balance?.totalAmount || 0) > 0;
            });
        }
    }, [balances]);

    const convertFromBalance = useMemo(() => {
        const balance = balances.find((b) => b.currency === convertFrom?.value) as BalanceItem & CollapsedBalances;
        return balance?.netPosition || balance?.totalAmount || 0;
    }, [balances, convertFrom?.value]);

    const inputRef = useRef(null);
    const { locale } = useLocale();

    const state = useNumberFieldState({ locale, formatOptions: convertedQtyFormatterConfig });
    const { groupProps, inputProps } = useNumberField({ label: 'convertFromInput' }, state, inputRef);

    useEffect(() => {
        if (amount !== state.numberValue && !isNaN(state.numberValue)) {
            setValue('amount', state.numberValue, { shouldValidate: true, shouldDirty: true });
        } else if (isNaN(state.numberValue)) {
            setValue('amount', null);
        }
    }, [state.numberValue]);

    useEffect(() => {
        if (amount !== state.numberValue) {
            state.setNumberValue(amount);
        }
    }, [amount]);

    return (
        <div className="w-full">
            <span className="text-brand-neutral-400 text-xs"> {lang.common.from}</span>
            <Controller
                control={control}
                name="convertFrom"
                render={({ field: { onChange }, fieldState: { error } }) => {
                    return (
                        <CurrencySelect
                            selected={convertFrom}
                            onSelectChange={(e) => {
                                setConvertFrom(e);
                                onChange(e);
                            }}
                            options={convertFromOptions}>
                            <div className="flex flex-col border border-brand-neutral-700 border-t-0 bg-brand-background-dark py-2 pb-4 px-3 rounded-b-md">
                                <div className="flex flex-col items-end justify-center h-32 w-full">
                                    <Controller
                                        control={control}
                                        name="amount"
                                        render={({ field: { name }, fieldState: { error } }) => {
                                            return (
                                                <div>
                                                    <div {...groupProps} className="relative">
                                                        <input
                                                            {...inputProps}
                                                            ref={mergeRefs(inputRef)}
                                                            className="w-full text-right pr-6 h-10 leading-none text-4xl font-bold tracking-wide bg-transparent text-brand-text focus-visible:outline-none placeholder:text-brand-neutral-400"
                                                            // value={amount || ''}
                                                            value={state.inputValue}
                                                            placeholder="0"
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    increments >= 100_000 &&
                                                                    ['k', 'm'].includes(e.key)
                                                                ) {
                                                                    e.preventDefault();
                                                                    const val = parseShortFormNumber(
                                                                        state.numberValue,
                                                                        e.key
                                                                    );
                                                                    if (!isNaN(val)) {
                                                                        setValue(name, val, {
                                                                            shouldValidate: true,
                                                                            shouldDirty: true
                                                                        });
                                                                        state.commit();
                                                                    }
                                                                }
                                                            }}
                                                            min={min_order_size}
                                                            max={convertFromBalance}
                                                        />
                                                        <div className="absolute right-0 top-0 -mt-0.5 h-full flex flex-col justify-center text-xs text-brand-text w-5">
                                                            <button
                                                                type="button"
                                                                className={cn('hover:text-brand-green-light px-0')}
                                                                onClick={() => {
                                                                    const prevValue = +(amount || 0);
                                                                    const nextValue = BigNumber(prevValue)
                                                                        .plus(increments)
                                                                        .toNumber();
                                                                    setValue(name, nextValue, { shouldValidate: true });
                                                                }}>
                                                                &#x25B2;
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={cn('hover:text-brand-green-light px-0')}
                                                                onClick={() => {
                                                                    const prevValue = +(amount || 0);
                                                                    const nextValue = BigNumber(prevValue)
                                                                        .minus(increments)
                                                                        .toNumber();
                                                                    if (nextValue <= +min_order_size)
                                                                        setValue(name, min_order_size, {
                                                                            shouldValidate: true
                                                                        });
                                                                    else
                                                                        setValue(name, nextValue, {
                                                                            shouldValidate: true
                                                                        });
                                                                }}>
                                                                &#x25BC;
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {convertFrom?.value && (
                                                        <ErrorMessage
                                                            className="text-end"
                                                            error={error}
                                                            isPadded={false}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        }}
                                    />
                                    <span className="text-2xs text-brand-neutral-400">
                                        {lang.lite.youHave}{' '}
                                        <u>
                                            {convertFromBalance} {convertFrom?.value}
                                        </u>
                                    </span>
                                </div>
                                <div className="flex items-center justify-center gap-3 text-xs">
                                    {[0.25, 0.5, 0.75, 1].map((percent) => (
                                        <PercentButton
                                            key={percent}
                                            percent={percent}
                                            onClick={() => {
                                                const val = onPercent(convertFromBalance, percent);
                                                setValue('amount', val ? val.toFixed(order_decimals) : val, {
                                                    shouldValidate: true
                                                });
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                            <ErrorMessage error={error} />
                        </CurrencySelect>
                    );
                }}
            />
        </div>
    );
};

export default ConvertFrom;

const PercentButton = ({ percent, onClick }: { percent: number; onClick: any }) => {
    const lang = useLanguage();
    return (
        <div
            className="p-1 px-3 text-brand-module-title bg-brand-accent hover:bg-brand-accent-light rounded-full cursor-pointer"
            onClick={onClick}>
            {percent === 1 ? lang.common.max : `${percent * 100}%`}
        </div>
    );
};

function onPercent(convertFromBalance: number, percent: number) {
    if (convertFromBalance > 0) return BigNumber(convertFromBalance).multipliedBy(percent).toNumber();
    else return 0;
}

export function convertToRate(bidAsk: { bid: number; ask: number } | undefined, type: string) {
    if (bidAsk?.ask && type === 'ask') return BigNumber(1).dividedBy(bidAsk.ask).toNumber();
    else if (bidAsk?.bid && type === 'bid') return BigNumber(bidAsk.bid).toNumber();
    else return 0;
}
